import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import CONFIG from '../config';
import { getToken } from '../utils/auth';

const UploadContext = createContext();

export const useUpload = () => {
    const context = useContext(UploadContext);
    if (!context) throw new Error('useUpload must be used within an UploadProvider');
    return context;
};

// Simple debounce for state updates to avoid React stutter
const debounceStates = new Map();
const debouncedUpdate = (id, updates, setter) => {
    if (debounceStates.has(id)) clearTimeout(debounceStates.get(id));
    debounceStates.set(id, setTimeout(() => {
        setter(prev => prev.map(t => t.id === id ? { ...t, ...updates } : t));
    }, 50));
};

// Helper to unique ID
const generateId = () => Math.random().toString(36).substr(2, 9);

export const UploadProvider = ({ children }) => {
    const [tasks, setTasks] = useState([]);

    useEffect(() => {
        const loadPersistedTasks = async () => {
            const token = getToken();
            if (!token) return;
            try {
                const res = await axios.get(`${CONFIG.API_URL}/api/admin/tasks`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const persisted = res.data.map(t => ({
                    ...t,
                    // If a task was 'running' but we just reloaded, it means it was interrupted
                    status: t.status === 'running' || t.status === 'pending' ? 'interrupted' : t.status,
                    message: (t.status === 'running' || t.status === 'pending') ? 'Task interrupted' : t.message
                }));
                setTasks(persisted);
            } catch (err) {
                console.error("Failed to load tasks:", err);
            }
        };
        loadPersistedTasks();
    }, []);

    const updateTask = useCallback(async (id, updates) => {
        setTasks(prev => prev.map(t => t.id === id ? { ...t, ...updates } : t));

        // Persist to DB
        const token = getToken();
        if (token) {
            try {
                await axios.patch(`${CONFIG.API_URL}/api/admin/tasks/${id}`, updates, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            } catch (err) {
                console.error("Failed to sync task update:", err);
            }
        }
    }, []);

    const updateTaskProgress = useCallback((id, updates) => {
        debouncedUpdate(id, updates, setTasks);

        // Persist progress to DB at milestones to avoid 5% stuck on reload
        if (updates.progress) {
            const p = Math.round(updates.progress);
            if ([25, 50, 75, 95].includes(p)) {
                updateTask(id, { progress: p });
            }
        }
    }, [updateTask]);

    const removeTask = useCallback(async (id) => {
        setTasks(prev => prev.filter(t => t.id !== id));
        const token = getToken();
        if (token) {
            try {
                await axios.delete(`${CONFIG.API_URL}/api/admin/tasks/${id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            } catch (err) {
                console.error("Failed to delete task from DB:", err);
            }
        }
    }, []);

    const clearTasks = useCallback(async () => {
        setTasks([]);
        const token = getToken();
        if (token) {
            try {
                await axios.delete(`${CONFIG.API_URL}/api/admin/tasks`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            } catch (err) {
                console.error("Failed to clear tasks from DB:", err);
            }
        }
    }, []);

    const fetchHistory = useCallback(async () => {
        const token = getToken();
        if (!token) return;
        try {
            const res = await axios.get(`${CONFIG.API_URL}/api/admin/tasks`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const persisted = res.data.map(t => ({
                ...t,
                status: t.status === 'running' || t.status === 'pending' ? 'interrupted' : t.status,
                message: (t.status === 'running' || t.status === 'pending') ? 'Task interrupted' : t.message
            }));

            // Merge: keep tasks that are currently running in memory
            setTasks(prev => {
                const runningIds = new Set(prev.filter(pt => pt.status === 'running').map(pt => pt.id));
                const updatedPersisted = persisted.filter(pt => !runningIds.has(pt.id));
                const currentRunning = prev.filter(pt => pt.status === 'running');
                return [...currentRunning, ...updatedPersisted];
            });
        } catch (err) {
            console.error("Failed to fetch task history:", err);
        }
    }, []);

    const uploadFileWithProgress = async (taskId, file, folder, weight, startProgress, totalWeight) => {
        try {
            const token = getToken();
            const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

            // /////////////////////////////////////////////////////////////
            // This line is assigned for X-Hostname, and it work with it
            // /////////////////////////////////////////////////////////////

            // const hostname = window.location.hostname;

            ///////////////////////////////////////////////////////////////
            // End This line is assigned for X-Hostname, and it work with it
            ///////////////////////////////////////////////////////////////
            const presignRes = await axios.post(
                `${CONFIG.API_URL}/api/admin/storage/presign`,
                { filename: file.name, mimetype: file.type, folder },
                { headers: authHeaders }
            );

            const { uploadUrl, fileUrl, method, headers, formData } = presignRes.data;

            const onUploadProgress = (progressEvent) => {
                const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                const taskProgress = startProgress + (percentCompleted * weight / totalWeight);
                updateTaskProgress(taskId, { progress: Math.min(taskProgress, 95) });
            };

            const sizeMB = file.size / (1024 * 1024);
            // Dynamic timeout: 30 seconds per MB, minimum 2 minutes, maximum 24 hours
            const uploadTimeout = Math.max(120000, Math.min(Math.floor(sizeMB * 30000), 86400000));

            if (formData) {
                const fd = new FormData();
                Object.entries(formData).forEach(([k, v]) => fd.append(k, v));
                fd.append("file", file);
                // Merge any headers returned by the presign step (e.g. x-api-key for the
                // owner storage server). We only set Content-Type explicitly because the
                // browser must generate the multipart boundary itself.
                const uploadHeaders = { ...(headers || {}) };
                delete uploadHeaders['Content-Type'];
                const res = await axios.post(uploadUrl, fd, {
                    headers: uploadHeaders,
                    onUploadProgress,
                    timeout: uploadTimeout
                });
                return res.data?.url || fileUrl;
            } else {
                await axios({
                    method: method || "PUT",
                    url: uploadUrl,
                    data: file,
                    headers: { "Content-Type": file.type, ...(headers || {}) },
                    onUploadProgress,
                    timeout: uploadTimeout
                });
            }

            return fileUrl;
        } catch (err) {
            console.error("Upload error detail:", err.response?.data || err.message);
            const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
            let errorMessage = `Failed to upload ${file.name} (${sizeMB}MB). `;

            if (err.code === 'ECONNABORTED') {
                errorMessage += "Connection timed out. Your internet may be too slow for this large file.";
            } else if (err.response?.status === 413) {
                errorMessage += "File too large for the storage provider.";
            } else if (err.response?.data?.error?.message) {
                errorMessage += err.response.data.error.message;
            } else {
                errorMessage += "Connection interrupted or storage limit reached.";
            }

            throw new Error(errorMessage);
        }
    };

    const runCreateTask = async (taskId, taskData, componentHelpers) => {
        const { compressImage } = componentHelpers;
        const { formData } = taskData;

        try {
            updateTask(taskId, { status: 'running', message: 'Preparing media...', progress: 5 });

            // Calculate weights
            const weights = { image: 10, thumb: 10, video: 50, api: 5 };
            let totalWeight = weights.api;
            if (formData.image) totalWeight += weights.image;
            if (formData.thumbnails?.length) totalWeight += weights.thumb * formData.thumbnails.length;
            if (formData.video) totalWeight += weights.video;

            let currentFinishedWeight = 0;
            let imageUrl = null;
            let videoUrl = null;
            const thumbnailUrls = [];

            // 1. Image
            if (formData.image) {
                updateTask(taskId, { message: 'Optimizing main image...' });
                const compressed = await compressImage(formData.image);
                imageUrl = await uploadFileWithProgress(taskId, compressed, "products/images", weights.image, (currentFinishedWeight / totalWeight) * 100, totalWeight);
                currentFinishedWeight += weights.image;
            }

            // 2. Thumbnails
            if (formData.thumbnails?.length) {
                for (let i = 0; i < formData.thumbnails.length; i++) {
                    updateTask(taskId, { message: `Uploading thumbnail ${i + 1}/${formData.thumbnails.length}...` });
                    const compressed = await compressImage(formData.thumbnails[i]);
                    const url = await uploadFileWithProgress(taskId, compressed, "products/thumbnails", weights.thumb, (currentFinishedWeight / totalWeight) * 100, totalWeight);
                    thumbnailUrls.push(url);
                    currentFinishedWeight += weights.thumb;
                }
            }

            // 3. Video
            if (formData.video) {
                updateTask(taskId, { message: 'Uploading video...' });
                videoUrl = await uploadFileWithProgress(taskId, formData.video, "products/videos", weights.video, (currentFinishedWeight / totalWeight) * 100, totalWeight);
                currentFinishedWeight += weights.video;
            }

            updateTask(taskId, { progress: 95, message: 'Finalizing product...' });

            const payload = {
                name: formData.name,
                name_ar: formData.name_ar,
                description: formData.description,
                description_ar: formData.description_ar,
                price: formData.price,
                price_egp: formData.price_egp,
                price_sdg: formData.price_sdg,
                color: formData.color,
                color_ar: formData.color_ar,
                material: formData.material,
                material_ar: formData.material_ar,
                dimensions: formData.dimensions,
                dimensions_ar: formData.dimensions_ar,
                imageUrl,
                videoUrl,
                thumbnailUrls: thumbnailUrls.length > 0 ? JSON.stringify(thumbnailUrls) : undefined
            };

            let finalCategories = [];
            if (formData.newCategory?.trim()) finalCategories.push(formData.newCategory.trim());
            if (Array.isArray(formData.category) && formData.category.length > 0) {
                const idSet = new Set(formData.category.map(x => String(x)));
                finalCategories.push(...Array.from(idSet));
            }
            payload.categories = JSON.stringify(Array.from(new Set(finalCategories.map(s => s.trim()).filter(Boolean))));

            const headers = {};
            const token = getToken();
            if (token) headers.Authorization = `Bearer ${token}`;

            await axios.post(`${CONFIG.API_URL}/api/products`, payload, { headers });
            updateTask(taskId, { status: 'success', progress: 100, message: 'Product created successfully!' });
        } catch (err) {
            console.error("Background task failed:", err);
            updateTask(taskId, { status: 'error', message: err.message || 'Upload failed' });
        }
    };

    const runEditTask = async (taskId, taskData, componentHelpers) => {
        const { compressImage } = componentHelpers;
        const { id, item, initialData } = taskData;
        const { newMainImage, newVideo, newThumbs, existingThumbs } = initialData;

        try {
            updateTask(taskId, { status: 'running', message: 'Processing changes...', progress: 5 });

            const weights = { image: 15, thumb: 15, video: 50, api: 5 };
            let totalWeight = weights.api;
            if (newMainImage) totalWeight += weights.image;
            if (newThumbs?.length) totalWeight += weights.thumb * newThumbs.length;
            if (newVideo) totalWeight += weights.video;

            let currentFinishedWeight = 0;
            let imageUrl = undefined;
            let videoUrl = undefined;
            const thumbnailUrls = [];

            if (newMainImage) {
                updateTask(taskId, { message: 'Optimizing main image...' });
                const compressed = await compressImage(newMainImage);
                imageUrl = await uploadFileWithProgress(taskId, compressed, "products/images", weights.image, (currentFinishedWeight / totalWeight) * 100, totalWeight);
                currentFinishedWeight += weights.image;
            }

            if (newThumbs?.length) {
                for (let i = 0; i < newThumbs.length; i++) {
                    updateTask(taskId, { message: `Uploading thumbnail ${i + 1}/${newThumbs.length}...` });
                    const compressed = await compressImage(newThumbs[i]);
                    const url = await uploadFileWithProgress(taskId, compressed, "products/thumbnails", weights.thumb, (currentFinishedWeight / totalWeight) * 100, totalWeight);
                    thumbnailUrls.push(url);
                    currentFinishedWeight += weights.thumb;
                }
            }

            if (newVideo) {
                updateTask(taskId, { message: 'Uploading video...' });
                videoUrl = await uploadFileWithProgress(taskId, newVideo, "products/videos", weights.video, (currentFinishedWeight / totalWeight) * 100, totalWeight);
                currentFinishedWeight += weights.video;
            }

            updateTask(taskId, { progress: 95, message: 'Finalizing update...' });

            const finalCategories = [];
            if (item.newCategory?.trim()) finalCategories.push(item.newCategory.trim());
            if (Array.isArray(item.category) && item.category.length) {
                const idSet = new Set(item.category.map(x => String(x)));
                finalCategories.push(...Array.from(idSet));
            }

            const payload = {
                name: item.name,
                name_ar: item.name_ar,
                description: item.description,
                description_ar: item.description_ar,
                price: item.price,
                price_egp: item.price_egp,
                price_sdg: item.price_sdg,
                color: item.color,
                color_ar: item.color_ar,
                material: item.material,
                material_ar: item.material_ar,
                dimensions: item.dimensions,
                dimensions_ar: item.dimensions_ar,
                categories: JSON.stringify(finalCategories),
                newCategory: item.newCategory,
                newCategoryAr: item.newCategoryAr,
                kept_thumbnails: JSON.stringify(existingThumbs),
                imageUrl: imageUrl || item.imageUrl,
                videoUrl: videoUrl || item.videoUrl,
                thumbnailUrls: thumbnailUrls.length > 0 ? JSON.stringify(thumbnailUrls) : undefined
            };

            const token = getToken();
            const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };

            await axios.put(`${CONFIG.API_URL}/api/products/${id}`, payload, { headers });
            updateTask(taskId, { status: 'success', progress: 100, message: 'Product updated successfully!' });
        } catch (err) {
            console.error("Background task failed:", err);
            updateTask(taskId, { status: 'error', message: err.message || 'Update failed' });
        }
    };

    const addTask = async (type, data, helpers) => {
        const id = generateId();
        const newTask = {
            id,
            task_type: type,
            name: data.formData?.name || data.item?.name || 'New Product',
            status: 'pending',
            progress: 0,
            message: 'Queued...',
            created_at: new Date()
        };

        setTasks(prev => [newTask, ...prev]);

        // Persist to DB immediately
        const token = getToken();
        if (token) {
            try {
                await axios.post(`${CONFIG.API_URL}/api/admin/tasks`, {
                    ...newTask,
                    payload: data // Store payload for potential retry
                }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            } catch (err) {
                console.error("Failed to persist new task:", err);
            }
        }

        if (type === 'CREATE') {
            runCreateTask(id, data, helpers);
        } else {
            runEditTask(id, data, helpers);
        }
        return id;
    };

    return (
        <UploadContext.Provider value={{ tasks, addTask, removeTask, clearTasks, fetchHistory }}>
            {children}
        </UploadContext.Provider>
    );
};























// // // import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
// // // import axios from 'axios';
// // // import CONFIG from '../config';
// // // import { getToken } from '../utils/auth';

// // // const UploadContext = createContext();

// // // export const useUpload = () => {
// // //     const context = useContext(UploadContext);
// // //     if (!context) throw new Error('useUpload must be used within an UploadProvider');
// // //     return context;
// // // };

// // // // Simple debounce for state updates to avoid React stutter
// // // const debounceStates = new Map();
// // // const debouncedUpdate = (id, updates, setter) => {
// // //     if (debounceStates.has(id)) clearTimeout(debounceStates.get(id));
// // //     debounceStates.set(id, setTimeout(() => {
// // //         setter(prev => prev.map(t => t.id === id ? { ...t, ...updates } : t));
// // //     }, 50));
// // // };

// // // // Helper to unique ID
// // // const generateId = () => Math.random().toString(36).substr(2, 9);

// // // export const UploadProvider = ({ children }) => {
// // //     const [tasks, setTasks] = useState([]);

// // //     useEffect(() => {
// // //         const loadPersistedTasks = async () => {
// // //             const token = getToken();
// // //             if (!token) return;
// // //             try {
// // //                 const res = await axios.get(`${CONFIG.API_URL}/api/admin/tasks`, {
// // //                     headers: { Authorization: `Bearer ${token}` }
// // //                 });
// // //                 const persisted = res.data.map(t => ({
// // //                     ...t,
// // //                     // If a task was 'running' but we just reloaded, it means it was interrupted
// // //                     status: t.status === 'running' || t.status === 'pending' ? 'interrupted' : t.status,
// // //                     message: (t.status === 'running' || t.status === 'pending') ? 'Task interrupted' : t.message
// // //                 }));
// // //                 setTasks(persisted);
// // //             } catch (err) {
// // //                 console.error("Failed to load tasks:", err);
// // //             }
// // //         };
// // //         loadPersistedTasks();
// // //     }, []);

// // //     const updateTask = useCallback(async (id, updates) => {
// // //         setTasks(prev => prev.map(t => t.id === id ? { ...t, ...updates } : t));

// // //         // Persist to DB
// // //         const token = getToken();
// // //         if (token) {
// // //             try {
// // //                 await axios.patch(`${CONFIG.API_URL}/api/admin/tasks/${id}`, updates, {
// // //                     headers: { Authorization: `Bearer ${token}` }
// // //                 });
// // //             } catch (err) {
// // //                 console.error("Failed to sync task update:", err);
// // //             }
// // //         }
// // //     }, []);

// // //     const updateTaskProgress = useCallback((id, updates) => {
// // //         debouncedUpdate(id, updates, setTasks);

// // //         // Persist progress to DB at milestones to avoid 5% stuck on reload
// // //         if (updates.progress) {
// // //             const p = Math.round(updates.progress);
// // //             if ([25, 50, 75, 95].includes(p)) {
// // //                 updateTask(id, { progress: p });
// // //             }
// // //         }
// // //     }, [updateTask]);

// // //     const removeTask = useCallback(async (id) => {
// // //         setTasks(prev => prev.filter(t => t.id !== id));
// // //         const token = getToken();
// // //         if (token) {
// // //             try {
// // //                 await axios.delete(`${CONFIG.API_URL}/api/admin/tasks/${id}`, {
// // //                     headers: { Authorization: `Bearer ${token}` }
// // //                 });
// // //             } catch (err) {
// // //                 console.error("Failed to delete task from DB:", err);
// // //             }
// // //         }
// // //     }, []);

// // //     const clearTasks = useCallback(async () => {
// // //         setTasks([]);
// // //         const token = getToken();
// // //         if (token) {
// // //             try {
// // //                 await axios.delete(`${CONFIG.API_URL}/api/admin/tasks`, {
// // //                     headers: { Authorization: `Bearer ${token}` }
// // //                 });
// // //             } catch (err) {
// // //                 console.error("Failed to clear tasks from DB:", err);
// // //             }
// // //         }
// // //     }, []);

// // //     const fetchHistory = useCallback(async () => {
// // //         const token = getToken();
// // //         if (!token) return;
// // //         try {
// // //             const res = await axios.get(`${CONFIG.API_URL}/api/admin/tasks`, {
// // //                 headers: { Authorization: `Bearer ${token}` }
// // //             });
// // //             const persisted = res.data.map(t => ({
// // //                 ...t,
// // //                 status: t.status === 'running' || t.status === 'pending' ? 'interrupted' : t.status,
// // //                 message: (t.status === 'running' || t.status === 'pending') ? 'Task interrupted' : t.message
// // //             }));

// // //             // Merge: keep tasks that are currently running in memory
// // //             setTasks(prev => {
// // //                 const runningIds = new Set(prev.filter(pt => pt.status === 'running').map(pt => pt.id));
// // //                 const updatedPersisted = persisted.filter(pt => !runningIds.has(pt.id));
// // //                 const currentRunning = prev.filter(pt => pt.status === 'running');
// // //                 return [...currentRunning, ...updatedPersisted];
// // //             });
// // //         } catch (err) {
// // //             console.error("Failed to fetch task history:", err);
// // //         }
// // //     }, []);

// // //     const uploadFileWithProgress = async (taskId, file, folder, weight, startProgress, totalWeight) => {
// // //         try {
// // //             const token = getToken();
// // //             const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

// // //             const presignRes = await axios.post(
// // //                 `${CONFIG.API_URL}/api/storage/presign`,
// // //                 { filename: file.name, mimetype: file.type, folder },
// // //                 { headers: authHeaders }
// // //             );

// // //             const { uploadUrl, fileUrl, method, headers, formData } = presignRes.data;

// // //             const onUploadProgress = (progressEvent) => {
// // //                 const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
// // //                 const taskProgress = startProgress + (percentCompleted * weight / totalWeight);
// // //                 updateTaskProgress(taskId, { progress: Math.min(taskProgress, 95) });
// // //             };

// // //             const sizeMB = file.size / (1024 * 1024);
// // //             // Dynamic timeout: 30 seconds per MB, minimum 2 minutes, maximum 24 hours
// // //             const uploadTimeout = Math.max(120000, Math.min(Math.floor(sizeMB * 30000), 86400000));

// // //             if (formData) {
// // //                 const fd = new FormData();
// // //                 Object.entries(formData).forEach(([k, v]) => fd.append(k, v));
// // //                 fd.append("file", file);
// // //                 await axios.post(uploadUrl, fd, {
// // //                     headers: { "Content-Type": "multipart/form-data" },
// // //                     onUploadProgress,
// // //                     timeout: uploadTimeout
// // //                 });
// // //             } else {
// // //                 await axios({
// // //                     method: method || "PUT",
// // //                     url: uploadUrl,
// // //                     data: file,
// // //                     headers: { "Content-Type": file.type, ...(headers || {}) },
// // //                     onUploadProgress,
// // //                     timeout: uploadTimeout
// // //                 });
// // //             }

// // //             return fileUrl;
// // //         } catch (err) {
// // //             console.error("Upload error detail:", err.response?.data || err.message);
// // //             const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
// // //             let errorMessage = `Failed to upload ${file.name} (${sizeMB}MB). `;

// // //             if (err.code === 'ECONNABORTED') {
// // //                 errorMessage += "Connection timed out. Your internet may be too slow for this large file.";
// // //             } else if (err.response?.status === 413) {
// // //                 errorMessage += "File too large for the storage provider.";
// // //             } else if (err.response?.data?.error?.message) {
// // //                 errorMessage += err.response.data.error.message;
// // //             } else {
// // //                 errorMessage += "Connection interrupted or storage limit reached.";
// // //             }

// // //             throw new Error(errorMessage);
// // //         }
// // //     };

// // //     const runCreateTask = async (taskId, taskData, componentHelpers) => {
// // //         const { compressImage } = componentHelpers;
// // //         const { formData, categories } = taskData;

// // //         try {
// // //             updateTask(taskId, { status: 'running', message: 'Preparing media...', progress: 5 });

// // //             // Calculate weights
// // //             const weights = { image: 10, thumb: 10, video: 50, api: 5 };
// // //             let totalWeight = weights.api;
// // //             if (formData.image) totalWeight += weights.image;
// // //             if (formData.thumbnails?.length) totalWeight += weights.thumb * formData.thumbnails.length;
// // //             if (formData.video) totalWeight += weights.video;

// // //             let currentFinishedWeight = 0;
// // //             let imageUrl = null;
// // //             let videoUrl = null;
// // //             const thumbnailUrls = [];

// // //             // 1. Image
// // //             if (formData.image) {
// // //                 updateTask(taskId, { message: 'Optimizing main image...' });
// // //                 const compressed = await compressImage(formData.image);
// // //                 imageUrl = await uploadFileWithProgress(taskId, compressed, "products/images", weights.image, (currentFinishedWeight / totalWeight) * 100, totalWeight);
// // //                 currentFinishedWeight += weights.image;
// // //             }

// // //             // 2. Thumbnails
// // //             if (formData.thumbnails?.length) {
// // //                 for (let i = 0; i < formData.thumbnails.length; i++) {
// // //                     updateTask(taskId, { message: `Uploading thumbnail ${i + 1}/${formData.thumbnails.length}...` });
// // //                     const compressed = await compressImage(formData.thumbnails[i]);
// // //                     const url = await uploadFileWithProgress(taskId, compressed, "products/thumbnails", weights.thumb, (currentFinishedWeight / totalWeight) * 100, totalWeight);
// // //                     thumbnailUrls.push(url);
// // //                     currentFinishedWeight += weights.thumb;
// // //                 }
// // //             }

// // //             // 3. Video
// // //             if (formData.video) {
// // //                 updateTask(taskId, { message: 'Uploading video...' });
// // //                 videoUrl = await uploadFileWithProgress(taskId, formData.video, "products/videos", weights.video, (currentFinishedWeight / totalWeight) * 100, totalWeight);
// // //                 currentFinishedWeight += weights.video;
// // //             }

// // //             updateTask(taskId, { progress: 95, message: 'Finalizing product...' });

// // //             const payload = {
// // //                 name: formData.name,
// // //                 name_ar: formData.name_ar,
// // //                 description: formData.description,
// // //                 description_ar: formData.description_ar,
// // //                 price: formData.price,
// // //                 price_egp: formData.price_egp,
// // //                 price_sdg: formData.price_sdg,
// // //                 color: formData.color,
// // //                 color_ar: formData.color_ar,
// // //                 material: formData.material,
// // //                 material_ar: formData.material_ar,
// // //                 dimensions: formData.dimensions,
// // //                 dimensions_ar: formData.dimensions_ar,
// // //                 imageUrl,
// // //                 videoUrl,
// // //                 thumbnailUrls: thumbnailUrls.length > 0 ? JSON.stringify(thumbnailUrls) : undefined
// // //             };

// // //             let finalCategories = [];
// // //             if (formData.newCategory?.trim()) finalCategories.push(formData.newCategory.trim());
// // //             if (Array.isArray(formData.category) && formData.category.length > 0) {
// // //                 const idSet = new Set(formData.category.map(x => Number(x)));
// // //                 const names = categories.filter(c => idSet.has(c.id)).map(c => c.name);
// // //                 finalCategories.push(...names);
// // //             }
// // //             payload.categories = JSON.stringify(Array.from(new Set(finalCategories.map(s => s.trim()).filter(Boolean))));

// // //             const headers = {};
// // //             const token = getToken();
// // //             if (token) headers.Authorization = `Bearer ${token}`;

// // //             await axios.post(`${CONFIG.API_URL}/api/products`, payload, { headers });
// // //             updateTask(taskId, { status: 'success', progress: 100, message: 'Product created successfully!' });
// // //         } catch (err) {
// // //             console.error("Background task failed:", err);
// // //             updateTask(taskId, { status: 'error', message: err.message || 'Upload failed' });
// // //         }
// // //     };

// // //     const runEditTask = async (taskId, taskData, componentHelpers) => {
// // //         const { compressImage } = componentHelpers;
// // //         const { id, item, initialData, categories: allCats } = taskData;
// // //         const { newMainImage, newVideo, newThumbs, existingThumbs } = initialData;

// // //         try {
// // //             updateTask(taskId, { status: 'running', message: 'Processing changes...', progress: 5 });

// // //             const weights = { image: 15, thumb: 15, video: 50, api: 5 };
// // //             let totalWeight = weights.api;
// // //             if (newMainImage) totalWeight += weights.image;
// // //             if (newThumbs?.length) totalWeight += weights.thumb * newThumbs.length;
// // //             if (newVideo) totalWeight += weights.video;

// // //             let currentFinishedWeight = 0;
// // //             let imageUrl = undefined;
// // //             let videoUrl = undefined;
// // //             const thumbnailUrls = [];

// // //             if (newMainImage) {
// // //                 updateTask(taskId, { message: 'Optimizing main image...' });
// // //                 const compressed = await compressImage(newMainImage);
// // //                 imageUrl = await uploadFileWithProgress(taskId, compressed, "products/images", weights.image, (currentFinishedWeight / totalWeight) * 100, totalWeight);
// // //                 currentFinishedWeight += weights.image;
// // //             }

// // //             if (newThumbs?.length) {
// // //                 for (let i = 0; i < newThumbs.length; i++) {
// // //                     updateTask(taskId, { message: `Uploading thumbnail ${i + 1}/${newThumbs.length}...` });
// // //                     const compressed = await compressImage(newThumbs[i]);
// // //                     const url = await uploadFileWithProgress(taskId, compressed, "products/thumbnails", weights.thumb, (currentFinishedWeight / totalWeight) * 100, totalWeight);
// // //                     thumbnailUrls.push(url);
// // //                     currentFinishedWeight += weights.thumb;
// // //                 }
// // //             }

// // //             if (newVideo) {
// // //                 updateTask(taskId, { message: 'Uploading video...' });
// // //                 videoUrl = await uploadFileWithProgress(taskId, newVideo, "products/videos", weights.video, (currentFinishedWeight / totalWeight) * 100, totalWeight);
// // //                 currentFinishedWeight += weights.video;
// // //             }

// // //             updateTask(taskId, { progress: 95, message: 'Finalizing update...' });

// // //             const finalCategories = [];
// // //             if (item.newCategory?.trim()) finalCategories.push(item.newCategory.trim());
// // //             if (Array.isArray(item.category) && item.category.length) {
// // //                 const idSet = new Set(item.category.map(x => Number(x)));
// // //                 const names = allCats.filter(c => idSet.has(c.id)).map(c => c.name);
// // //                 finalCategories.push(...names);
// // //             }

// // //             const payload = {
// // //                 name: item.name,
// // //                 name_ar: item.name_ar,
// // //                 description: item.description,
// // //                 description_ar: item.description_ar,
// // //                 price: item.price,
// // //                 price_egp: item.price_egp,
// // //                 price_sdg: item.price_sdg,
// // //                 color: item.color,
// // //                 color_ar: item.color_ar,
// // //                 material: item.material,
// // //                 material_ar: item.material_ar,
// // //                 dimensions: item.dimensions,
// // //                 dimensions_ar: item.dimensions_ar,
// // //                 categories: JSON.stringify(finalCategories),
// // //                 newCategory: item.newCategory,
// // //                 newCategoryAr: item.newCategoryAr,
// // //                 kept_thumbnails: JSON.stringify(existingThumbs),
// // //                 imageUrl: imageUrl || item.imageUrl,
// // //                 videoUrl: videoUrl || item.videoUrl,
// // //                 thumbnailUrls: thumbnailUrls.length > 0 ? JSON.stringify(thumbnailUrls) : undefined
// // //             };

// // //             const token = getToken();
// // //             const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };

// // //             await axios.put(`${CONFIG.API_URL}/api/products/${id}`, payload, { headers });
// // //             updateTask(taskId, { status: 'success', progress: 100, message: 'Product updated successfully!' });
// // //         } catch (err) {
// // //             console.error("Background task failed:", err);
// // //             updateTask(taskId, { status: 'error', message: err.message || 'Update failed' });
// // //         }
// // //     };

// // //     const addTask = async (type, data, helpers) => {
// // //         const id = generateId();
// // //         const newTask = {
// // //             id,
// // //             task_type: type,
// // //             name: data.formData?.name || data.item?.name || 'New Product',
// // //             status: 'pending',
// // //             progress: 0,
// // //             message: 'Queued...',
// // //             created_at: new Date()
// // //         };

// // //         setTasks(prev => [newTask, ...prev]);

// // //         // Persist to DB immediately
// // //         const token = getToken();
// // //         if (token) {
// // //             try {
// // //                 await axios.post(`${CONFIG.API_URL}/api/admin/tasks`, {
// // //                     ...newTask,
// // //                     payload: data // Store payload for potential retry
// // //                 }, {
// // //                     headers: { Authorization: `Bearer ${token}` }
// // //                 });
// // //             } catch (err) {
// // //                 console.error("Failed to persist new task:", err);
// // //             }
// // //         }

// // //         if (type === 'CREATE') {
// // //             runCreateTask(id, data, helpers);
// // //         } else {
// // //             runEditTask(id, data, helpers);
// // //         }
// // //         return id;
// // //     };

// // //     return (
// // //         <UploadContext.Provider value={{ tasks, addTask, removeTask, clearTasks, fetchHistory }}>
// // //             {children}
// // //         </UploadContext.Provider>
// // //     );
// // // };








// // import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
// // import axios from 'axios';
// // import CONFIG from '../config';
// // import { getToken } from '../utils/auth';

// // const UploadContext = createContext();

// // export const useUpload = () => {
// //     const context = useContext(UploadContext);
// //     if (!context) throw new Error('useUpload must be used within an UploadProvider');
// //     return context;
// // };

// // // Simple debounce for state updates to avoid React stutter
// // const debounceStates = new Map();
// // const debouncedUpdate = (id, updates, setter) => {
// //     if (debounceStates.has(id)) clearTimeout(debounceStates.get(id));
// //     debounceStates.set(id, setTimeout(() => {
// //         setter(prev => prev.map(t => t.id === id ? { ...t, ...updates } : t));
// //     }, 50));
// // };

// // // Helper to unique ID
// // const generateId = () => Math.random().toString(36).substr(2, 9);

// // export const UploadProvider = ({ children }) => {
// //     const [tasks, setTasks] = useState([]);

// //     useEffect(() => {
// //         const loadPersistedTasks = async () => {
// //             const token = getToken();
// //             if (!token) return;
// //             try {
// //                 const res = await axios.get(`${CONFIG.API_URL}/api/admin/tasks`, {
// //                     headers: { Authorization: `Bearer ${token}` }
// //                 });
// //                 const persisted = res.data.map(t => ({
// //                     ...t,
// //                     // If a task was 'running' but we just reloaded, it means it was interrupted
// //                     status: t.status === 'running' || t.status === 'pending' ? 'interrupted' : t.status,
// //                     message: (t.status === 'running' || t.status === 'pending') ? 'Task interrupted' : t.message
// //                 }));
// //                 setTasks(persisted);
// //             } catch (err) {
// //                 console.error("Failed to load tasks:", err);
// //             }
// //         };
// //         loadPersistedTasks();
// //     }, []);

// //     const updateTask = useCallback(async (id, updates) => {
// //         setTasks(prev => prev.map(t => t.id === id ? { ...t, ...updates } : t));

// //         // Persist to DB
// //         const token = getToken();
// //         if (token) {
// //             try {
// //                 await axios.patch(`${CONFIG.API_URL}/api/admin/tasks/${id}`, updates, {
// //                     headers: { Authorization: `Bearer ${token}` }
// //                 });
// //             } catch (err) {
// //                 console.error("Failed to sync task update:", err);
// //             }
// //         }
// //     }, []);

// //     const updateTaskProgress = useCallback((id, updates) => {
// //         debouncedUpdate(id, updates, setTasks);

// //         // Persist progress to DB at milestones to avoid 5% stuck on reload
// //         if (updates.progress) {
// //             const p = Math.round(updates.progress);
// //             if ([25, 50, 75, 95].includes(p)) {
// //                 updateTask(id, { progress: p });
// //             }
// //         }
// //     }, [updateTask]);

// //     const removeTask = useCallback(async (id) => {
// //         setTasks(prev => prev.filter(t => t.id !== id));
// //         const token = getToken();
// //         if (token) {
// //             try {
// //                 await axios.delete(`${CONFIG.API_URL}/api/admin/tasks/${id}`, {
// //                     headers: { Authorization: `Bearer ${token}` }
// //                 });
// //             } catch (err) {
// //                 console.error("Failed to delete task from DB:", err);
// //             }
// //         }
// //     }, []);

// //     const clearTasks = useCallback(async () => {
// //         setTasks([]);
// //         const token = getToken();
// //         if (token) {
// //             try {
// //                 await axios.delete(`${CONFIG.API_URL}/api/admin/tasks`, {
// //                     headers: { Authorization: `Bearer ${token}` }
// //                 });
// //             } catch (err) {
// //                 console.error("Failed to clear tasks from DB:", err);
// //             }
// //         }
// //     }, []);

// //     const fetchHistory = useCallback(async () => {
// //         const token = getToken();
// //         if (!token) return;
// //         try {
// //             const res = await axios.get(`${CONFIG.API_URL}/api/admin/tasks`, {
// //                 headers: { Authorization: `Bearer ${token}` }
// //             });
// //             const persisted = res.data.map(t => ({
// //                 ...t,
// //                 status: t.status === 'running' || t.status === 'pending' ? 'interrupted' : t.status,
// //                 message: (t.status === 'running' || t.status === 'pending') ? 'Task interrupted' : t.message
// //             }));

// //             // Merge: keep tasks that are currently running in memory
// //             setTasks(prev => {
// //                 const runningIds = new Set(prev.filter(pt => pt.status === 'running').map(pt => pt.id));
// //                 const updatedPersisted = persisted.filter(pt => !runningIds.has(pt.id));
// //                 const currentRunning = prev.filter(pt => pt.status === 'running');
// //                 return [...currentRunning, ...updatedPersisted];
// //             });
// //         } catch (err) {
// //             console.error("Failed to fetch task history:", err);
// //         }
// //     }, []);

// //     const uploadFileWithProgress = async (taskId, file, folder, weight, startProgress, totalWeight) => {
// //         try {
// //             const token = getToken();
// //             const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

// //             const hostname = window.location.hostname;
// //             const presignRes = await axios.post(
// //                 `${CONFIG.API_URL}/api/admin/storage/presign`,
// //                 { filename: file.name, mimetype: file.type, folder },
// //                 { headers: { ...authHeaders, 'X-Hostname': hostname } }
// //             );

// //             const { uploadUrl, fileUrl, method, headers, formData } = presignRes.data;

// //             const onUploadProgress = (progressEvent) => {
// //                 const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
// //                 const taskProgress = startProgress + (percentCompleted * weight / totalWeight);
// //                 updateTaskProgress(taskId, { progress: Math.min(taskProgress, 95) });
// //             };

// //             const sizeMB = file.size / (1024 * 1024);
// //             // Dynamic timeout: 30 seconds per MB, minimum 2 minutes, maximum 24 hours
// //             const uploadTimeout = Math.max(120000, Math.min(Math.floor(sizeMB * 30000), 86400000));

// //             if (formData) {
// //                 const fd = new FormData();
// //                 Object.entries(formData).forEach(([k, v]) => fd.append(k, v));
// //                 fd.append("file", file);
// //                 const res = await axios.post(uploadUrl, fd, {
// //                     headers: { "Content-Type": "multipart/form-data" },
// //                     onUploadProgress,
// //                     timeout: uploadTimeout
// //                 });
// //                 return res.data?.url || fileUrl;
// //             } else {
// //                 await axios({
// //                     method: method || "PUT",
// //                     url: uploadUrl,
// //                     data: file,
// //                     headers: { "Content-Type": file.type, ...(headers || {}) },
// //                     onUploadProgress,
// //                     timeout: uploadTimeout
// //                 });
// //             }

// //             return fileUrl;
// //         } catch (err) {
// //             console.error("Upload error detail:", err.response?.data || err.message);
// //             const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
// //             let errorMessage = `Failed to upload ${file.name} (${sizeMB}MB). `;

// //             if (err.code === 'ECONNABORTED') {
// //                 errorMessage += "Connection timed out. Your internet may be too slow for this large file.";
// //             } else if (err.response?.status === 413) {
// //                 errorMessage += "File too large for the storage provider.";
// //             } else if (err.response?.data?.error?.message) {
// //                 errorMessage += err.response.data.error.message;
// //             } else {
// //                 errorMessage += "Connection interrupted or storage limit reached.";
// //             }

// //             throw new Error(errorMessage);
// //         }
// //     };

// //     const runCreateTask = async (taskId, taskData, componentHelpers) => {
// //         const { compressImage } = componentHelpers;
// //         const { formData } = taskData;

// //         try {
// //             updateTask(taskId, { status: 'running', message: 'Preparing media...', progress: 5 });

// //             // Calculate weights
// //             const weights = { image: 10, thumb: 10, video: 50, api: 5 };
// //             let totalWeight = weights.api;
// //             if (formData.image) totalWeight += weights.image;
// //             if (formData.thumbnails?.length) totalWeight += weights.thumb * formData.thumbnails.length;
// //             if (formData.video) totalWeight += weights.video;

// //             let currentFinishedWeight = 0;
// //             let imageUrl = null;
// //             let videoUrl = null;
// //             const thumbnailUrls = [];

// //             // 1. Image
// //             if (formData.image) {
// //                 updateTask(taskId, { message: 'Optimizing main image...' });
// //                 const compressed = await compressImage(formData.image);
// //                 imageUrl = await uploadFileWithProgress(taskId, compressed, "products/images", weights.image, (currentFinishedWeight / totalWeight) * 100, totalWeight);
// //                 currentFinishedWeight += weights.image;
// //             }

// //             // 2. Thumbnails
// //             if (formData.thumbnails?.length) {
// //                 for (let i = 0; i < formData.thumbnails.length; i++) {
// //                     updateTask(taskId, { message: `Uploading thumbnail ${i + 1}/${formData.thumbnails.length}...` });
// //                     const compressed = await compressImage(formData.thumbnails[i]);
// //                     const url = await uploadFileWithProgress(taskId, compressed, "products/thumbnails", weights.thumb, (currentFinishedWeight / totalWeight) * 100, totalWeight);
// //                     thumbnailUrls.push(url);
// //                     currentFinishedWeight += weights.thumb;
// //                 }
// //             }

// //             // 3. Video
// //             if (formData.video) {
// //                 updateTask(taskId, { message: 'Uploading video...' });
// //                 videoUrl = await uploadFileWithProgress(taskId, formData.video, "products/videos", weights.video, (currentFinishedWeight / totalWeight) * 100, totalWeight);
// //                 currentFinishedWeight += weights.video;
// //             }

// //             updateTask(taskId, { progress: 95, message: 'Finalizing product...' });

// //             const payload = {
// //                 name: formData.name,
// //                 name_ar: formData.name_ar,
// //                 description: formData.description,
// //                 description_ar: formData.description_ar,
// //                 price: formData.price,
// //                 price_egp: formData.price_egp,
// //                 price_sdg: formData.price_sdg,
// //                 color: formData.color,
// //                 color_ar: formData.color_ar,
// //                 material: formData.material,
// //                 material_ar: formData.material_ar,
// //                 dimensions: formData.dimensions,
// //                 dimensions_ar: formData.dimensions_ar,
// //                 imageUrl,
// //                 videoUrl,
// //                 thumbnailUrls: thumbnailUrls.length > 0 ? JSON.stringify(thumbnailUrls) : undefined
// //             };

// //             let finalCategories = [];
// //             if (formData.newCategory?.trim()) finalCategories.push(formData.newCategory.trim());
// //             if (Array.isArray(formData.category) && formData.category.length > 0) {
// //                 const idSet = new Set(formData.category.map(x => String(x)));
// //                 finalCategories.push(...Array.from(idSet));
// //             }
// //             payload.categories = JSON.stringify(Array.from(new Set(finalCategories.map(s => s.trim()).filter(Boolean))));

// //             const headers = {};
// //             const token = getToken();
// //             if (token) headers.Authorization = `Bearer ${token}`;

// //             await axios.post(`${CONFIG.API_URL}/api/products`, payload, { headers });
// //             updateTask(taskId, { status: 'success', progress: 100, message: 'Product created successfully!' });
// //         } catch (err) {
// //             console.error("Background task failed:", err);
// //             updateTask(taskId, { status: 'error', message: err.message || 'Upload failed' });
// //         }
// //     };

// //     const runEditTask = async (taskId, taskData, componentHelpers) => {
// //         const { compressImage } = componentHelpers;
// //         const { id, item, initialData } = taskData;
// //         const { newMainImage, newVideo, newThumbs, existingThumbs } = initialData;

// //         try {
// //             updateTask(taskId, { status: 'running', message: 'Processing changes...', progress: 5 });

// //             const weights = { image: 15, thumb: 15, video: 50, api: 5 };
// //             let totalWeight = weights.api;
// //             if (newMainImage) totalWeight += weights.image;
// //             if (newThumbs?.length) totalWeight += weights.thumb * newThumbs.length;
// //             if (newVideo) totalWeight += weights.video;

// //             let currentFinishedWeight = 0;
// //             let imageUrl = undefined;
// //             let videoUrl = undefined;
// //             const thumbnailUrls = [];

// //             if (newMainImage) {
// //                 updateTask(taskId, { message: 'Optimizing main image...' });
// //                 const compressed = await compressImage(newMainImage);
// //                 imageUrl = await uploadFileWithProgress(taskId, compressed, "products/images", weights.image, (currentFinishedWeight / totalWeight) * 100, totalWeight);
// //                 currentFinishedWeight += weights.image;
// //             }

// //             if (newThumbs?.length) {
// //                 for (let i = 0; i < newThumbs.length; i++) {
// //                     updateTask(taskId, { message: `Uploading thumbnail ${i + 1}/${newThumbs.length}...` });
// //                     const compressed = await compressImage(newThumbs[i]);
// //                     const url = await uploadFileWithProgress(taskId, compressed, "products/thumbnails", weights.thumb, (currentFinishedWeight / totalWeight) * 100, totalWeight);
// //                     thumbnailUrls.push(url);
// //                     currentFinishedWeight += weights.thumb;
// //                 }
// //             }

// //             if (newVideo) {
// //                 updateTask(taskId, { message: 'Uploading video...' });
// //                 videoUrl = await uploadFileWithProgress(taskId, newVideo, "products/videos", weights.video, (currentFinishedWeight / totalWeight) * 100, totalWeight);
// //                 currentFinishedWeight += weights.video;
// //             }

// //             updateTask(taskId, { progress: 95, message: 'Finalizing update...' });

// //             const finalCategories = [];
// //             if (item.newCategory?.trim()) finalCategories.push(item.newCategory.trim());
// //             if (Array.isArray(item.category) && item.category.length) {
// //                 const idSet = new Set(item.category.map(x => String(x)));
// //                 finalCategories.push(...Array.from(idSet));
// //             }

// //             const payload = {
// //                 name: item.name,
// //                 name_ar: item.name_ar,
// //                 description: item.description,
// //                 description_ar: item.description_ar,
// //                 price: item.price,
// //                 price_egp: item.price_egp,
// //                 price_sdg: item.price_sdg,
// //                 color: item.color,
// //                 color_ar: item.color_ar,
// //                 material: item.material,
// //                 material_ar: item.material_ar,
// //                 dimensions: item.dimensions,
// //                 dimensions_ar: item.dimensions_ar,
// //                 categories: JSON.stringify(finalCategories),
// //                 newCategory: item.newCategory,
// //                 newCategoryAr: item.newCategoryAr,
// //                 kept_thumbnails: JSON.stringify(existingThumbs),
// //                 imageUrl: imageUrl || item.imageUrl,
// //                 videoUrl: videoUrl || item.videoUrl,
// //                 thumbnailUrls: thumbnailUrls.length > 0 ? JSON.stringify(thumbnailUrls) : undefined
// //             };

// //             const token = getToken();
// //             const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };

// //             await axios.put(`${CONFIG.API_URL}/api/products/${id}`, payload, { headers });
// //             updateTask(taskId, { status: 'success', progress: 100, message: 'Product updated successfully!' });
// //         } catch (err) {
// //             console.error("Background task failed:", err);
// //             updateTask(taskId, { status: 'error', message: err.message || 'Update failed' });
// //         }
// //     };

// //     const addTask = async (type, data, helpers) => {
// //         const id = generateId();
// //         const newTask = {
// //             id,
// //             task_type: type,
// //             name: data.formData?.name || data.item?.name || 'New Product',
// //             status: 'pending',
// //             progress: 0,
// //             message: 'Queued...',
// //             created_at: new Date()
// //         };

// //         setTasks(prev => [newTask, ...prev]);

// //         // Persist to DB immediately
// //         const token = getToken();
// //         if (token) {
// //             try {
// //                 await axios.post(`${CONFIG.API_URL}/api/admin/tasks`, {
// //                     ...newTask,
// //                     payload: data // Store payload for potential retry
// //                 }, {
// //                     headers: { Authorization: `Bearer ${token}` }
// //                 });
// //             } catch (err) {
// //                 console.error("Failed to persist new task:", err);
// //             }
// //         }

// //         if (type === 'CREATE') {
// //             runCreateTask(id, data, helpers);
// //         } else {
// //             runEditTask(id, data, helpers);
// //         }
// //         return id;
// //     };

// //     return (
// //         <UploadContext.Provider value={{ tasks, addTask, removeTask, clearTasks, fetchHistory }}>
// //             {children}
// //         </UploadContext.Provider>
// //     );
// // };













































// // import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
// // import axios from 'axios';
// // import CONFIG from '../config';
// // import { getToken } from '../utils/auth';

// // const UploadContext = createContext();

// // export const useUpload = () => {
// //     const context = useContext(UploadContext);
// //     if (!context) throw new Error('useUpload must be used within an UploadProvider');
// //     return context;
// // };

// // // Simple debounce for state updates to avoid React stutter
// // const debounceStates = new Map();
// // const debouncedUpdate = (id, updates, setter) => {
// //     if (debounceStates.has(id)) clearTimeout(debounceStates.get(id));
// //     debounceStates.set(id, setTimeout(() => {
// //         setter(prev => prev.map(t => t.id === id ? { ...t, ...updates } : t));
// //     }, 50));
// // };

// // // Helper to unique ID
// // const generateId = () => Math.random().toString(36).substr(2, 9);

// // export const UploadProvider = ({ children }) => {
// //     const [tasks, setTasks] = useState([]);

// //     useEffect(() => {
// //         const loadPersistedTasks = async () => {
// //             const token = getToken();
// //             if (!token) return;
// //             try {
// //                 const res = await axios.get(`${CONFIG.API_URL}/api/admin/tasks`, {
// //                     headers: { Authorization: `Bearer ${token}` }
// //                 });
// //                 const persisted = res.data.map(t => ({
// //                     ...t,
// //                     // If a task was 'running' but we just reloaded, it means it was interrupted
// //                     status: t.status === 'running' || t.status === 'pending' ? 'interrupted' : t.status,
// //                     message: (t.status === 'running' || t.status === 'pending') ? 'Task interrupted' : t.message
// //                 }));
// //                 setTasks(persisted);
// //             } catch (err) {
// //                 console.error("Failed to load tasks:", err);
// //             }
// //         };
// //         loadPersistedTasks();
// //     }, []);

// //     const updateTask = useCallback(async (id, updates) => {
// //         setTasks(prev => prev.map(t => t.id === id ? { ...t, ...updates } : t));

// //         // Persist to DB
// //         const token = getToken();
// //         if (token) {
// //             try {
// //                 await axios.patch(`${CONFIG.API_URL}/api/admin/tasks/${id}`, updates, {
// //                     headers: { Authorization: `Bearer ${token}` }
// //                 });
// //             } catch (err) {
// //                 console.error("Failed to sync task update:", err);
// //             }
// //         }
// //     }, []);

// //     const updateTaskProgress = useCallback((id, updates) => {
// //         debouncedUpdate(id, updates, setTasks);

// //         // Persist progress to DB at milestones to avoid 5% stuck on reload
// //         if (updates.progress) {
// //             const p = Math.round(updates.progress);
// //             if ([25, 50, 75, 95].includes(p)) {
// //                 updateTask(id, { progress: p });
// //             }
// //         }
// //     }, [updateTask]);

// //     const removeTask = useCallback(async (id) => {
// //         setTasks(prev => prev.filter(t => t.id !== id));
// //         const token = getToken();
// //         if (token) {
// //             try {
// //                 await axios.delete(`${CONFIG.API_URL}/api/admin/tasks/${id}`, {
// //                     headers: { Authorization: `Bearer ${token}` }
// //                 });
// //             } catch (err) {
// //                 console.error("Failed to delete task from DB:", err);
// //             }
// //         }
// //     }, []);

// //     const clearTasks = useCallback(async () => {
// //         setTasks([]);
// //         const token = getToken();
// //         if (token) {
// //             try {
// //                 await axios.delete(`${CONFIG.API_URL}/api/admin/tasks`, {
// //                     headers: { Authorization: `Bearer ${token}` }
// //                 });
// //             } catch (err) {
// //                 console.error("Failed to clear tasks from DB:", err);
// //             }
// //         }
// //     }, []);

// //     const fetchHistory = useCallback(async () => {
// //         const token = getToken();
// //         if (!token) return;
// //         try {
// //             const res = await axios.get(`${CONFIG.API_URL}/api/admin/tasks`, {
// //                 headers: { Authorization: `Bearer ${token}` }
// //             });
// //             const persisted = res.data.map(t => ({
// //                 ...t,
// //                 status: t.status === 'running' || t.status === 'pending' ? 'interrupted' : t.status,
// //                 message: (t.status === 'running' || t.status === 'pending') ? 'Task interrupted' : t.message
// //             }));

// //             // Merge: keep tasks that are currently running in memory
// //             setTasks(prev => {
// //                 const runningIds = new Set(prev.filter(pt => pt.status === 'running').map(pt => pt.id));
// //                 const updatedPersisted = persisted.filter(pt => !runningIds.has(pt.id));
// //                 const currentRunning = prev.filter(pt => pt.status === 'running');
// //                 return [...currentRunning, ...updatedPersisted];
// //             });
// //         } catch (err) {
// //             console.error("Failed to fetch task history:", err);
// //         }
// //     }, []);

// //     const uploadFileWithProgress = async (taskId, file, folder, weight, startProgress, totalWeight) => {
// //         try {
// //             const token = getToken();
// //             const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

// //             const presignRes = await axios.post(
// //                 `${CONFIG.API_URL}/api/storage/presign`,
// //                 { filename: file.name, mimetype: file.type, folder },
// //                 { headers: authHeaders }
// //             );

// //             const { uploadUrl, fileUrl, method, headers, formData } = presignRes.data;

// //             const onUploadProgress = (progressEvent) => {
// //                 const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
// //                 const taskProgress = startProgress + (percentCompleted * weight / totalWeight);
// //                 updateTaskProgress(taskId, { progress: Math.min(taskProgress, 95) });
// //             };

// //             const sizeMB = file.size / (1024 * 1024);
// //             // Dynamic timeout: 30 seconds per MB, minimum 2 minutes, maximum 24 hours
// //             const uploadTimeout = Math.max(120000, Math.min(Math.floor(sizeMB * 30000), 86400000));

// //             if (formData) {
// //                 const fd = new FormData();
// //                 Object.entries(formData).forEach(([k, v]) => fd.append(k, v));
// //                 fd.append("file", file);
// //                 await axios.post(uploadUrl, fd, {
// //                     headers: { "Content-Type": "multipart/form-data" },
// //                     onUploadProgress,
// //                     timeout: uploadTimeout
// //                 });
// //             } else {
// //                 await axios({
// //                     method: method || "PUT",
// //                     url: uploadUrl,
// //                     data: file,
// //                     headers: { "Content-Type": file.type, ...(headers || {}) },
// //                     onUploadProgress,
// //                     timeout: uploadTimeout
// //                 });
// //             }

// //             return fileUrl;
// //         } catch (err) {
// //             console.error("Upload error detail:", err.response?.data || err.message);
// //             const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
// //             let errorMessage = `Failed to upload ${file.name} (${sizeMB}MB). `;

// //             if (err.code === 'ECONNABORTED') {
// //                 errorMessage += "Connection timed out. Your internet may be too slow for this large file.";
// //             } else if (err.response?.status === 413) {
// //                 errorMessage += "File too large for the storage provider.";
// //             } else if (err.response?.data?.error?.message) {
// //                 errorMessage += err.response.data.error.message;
// //             } else {
// //                 errorMessage += "Connection interrupted or storage limit reached.";
// //             }

// //             throw new Error(errorMessage);
// //         }
// //     };

// //     const runCreateTask = async (taskId, taskData, componentHelpers) => {
// //         const { compressImage } = componentHelpers;
// //         const { formData, categories } = taskData;

// //         try {
// //             updateTask(taskId, { status: 'running', message: 'Preparing media...', progress: 5 });

// //             // Calculate weights
// //             const weights = { image: 10, thumb: 10, video: 50, api: 5 };
// //             let totalWeight = weights.api;
// //             if (formData.image) totalWeight += weights.image;
// //             if (formData.thumbnails?.length) totalWeight += weights.thumb * formData.thumbnails.length;
// //             if (formData.video) totalWeight += weights.video;

// //             let currentFinishedWeight = 0;
// //             let imageUrl = null;
// //             let videoUrl = null;
// //             const thumbnailUrls = [];

// //             // 1. Image
// //             if (formData.image) {
// //                 updateTask(taskId, { message: 'Optimizing main image...' });
// //                 const compressed = await compressImage(formData.image);
// //                 imageUrl = await uploadFileWithProgress(taskId, compressed, "products/images", weights.image, (currentFinishedWeight / totalWeight) * 100, totalWeight);
// //                 currentFinishedWeight += weights.image;
// //             }

// //             // 2. Thumbnails
// //             if (formData.thumbnails?.length) {
// //                 for (let i = 0; i < formData.thumbnails.length; i++) {
// //                     updateTask(taskId, { message: `Uploading thumbnail ${i + 1}/${formData.thumbnails.length}...` });
// //                     const compressed = await compressImage(formData.thumbnails[i]);
// //                     const url = await uploadFileWithProgress(taskId, compressed, "products/thumbnails", weights.thumb, (currentFinishedWeight / totalWeight) * 100, totalWeight);
// //                     thumbnailUrls.push(url);
// //                     currentFinishedWeight += weights.thumb;
// //                 }
// //             }

// //             // 3. Video
// //             if (formData.video) {
// //                 updateTask(taskId, { message: 'Uploading video...' });
// //                 videoUrl = await uploadFileWithProgress(taskId, formData.video, "products/videos", weights.video, (currentFinishedWeight / totalWeight) * 100, totalWeight);
// //                 currentFinishedWeight += weights.video;
// //             }

// //             updateTask(taskId, { progress: 95, message: 'Finalizing product...' });

// //             const payload = {
// //                 name: formData.name,
// //                 name_ar: formData.name_ar,
// //                 description: formData.description,
// //                 description_ar: formData.description_ar,
// //                 price: formData.price,
// //                 price_egp: formData.price_egp,
// //                 price_sdg: formData.price_sdg,
// //                 color: formData.color,
// //                 color_ar: formData.color_ar,
// //                 material: formData.material,
// //                 material_ar: formData.material_ar,
// //                 dimensions: formData.dimensions,
// //                 dimensions_ar: formData.dimensions_ar,
// //                 imageUrl,
// //                 videoUrl,
// //                 thumbnailUrls: thumbnailUrls.length > 0 ? JSON.stringify(thumbnailUrls) : undefined
// //             };

// //             let finalCategories = [];
// //             if (formData.newCategory?.trim()) finalCategories.push(formData.newCategory.trim());
// //             if (Array.isArray(formData.category) && formData.category.length > 0) {
// //                 const idSet = new Set(formData.category.map(x => Number(x)));
// //                 const names = categories.filter(c => idSet.has(c.id)).map(c => c.name);
// //                 finalCategories.push(...names);
// //             }
// //             payload.categories = JSON.stringify(Array.from(new Set(finalCategories.map(s => s.trim()).filter(Boolean))));

// //             const headers = {};
// //             const token = getToken();
// //             if (token) headers.Authorization = `Bearer ${token}`;

// //             await axios.post(`${CONFIG.API_URL}/api/products`, payload, { headers });
// //             updateTask(taskId, { status: 'success', progress: 100, message: 'Product created successfully!' });
// //         } catch (err) {
// //             console.error("Background task failed:", err);
// //             updateTask(taskId, { status: 'error', message: err.message || 'Upload failed' });
// //         }
// //     };

// //     const runEditTask = async (taskId, taskData, componentHelpers) => {
// //         const { compressImage } = componentHelpers;
// //         const { id, item, initialData, categories: allCats } = taskData;
// //         const { newMainImage, newVideo, newThumbs, existingThumbs } = initialData;

// //         try {
// //             updateTask(taskId, { status: 'running', message: 'Processing changes...', progress: 5 });

// //             const weights = { image: 15, thumb: 15, video: 50, api: 5 };
// //             let totalWeight = weights.api;
// //             if (newMainImage) totalWeight += weights.image;
// //             if (newThumbs?.length) totalWeight += weights.thumb * newThumbs.length;
// //             if (newVideo) totalWeight += weights.video;

// //             let currentFinishedWeight = 0;
// //             let imageUrl = undefined;
// //             let videoUrl = undefined;
// //             const thumbnailUrls = [];

// //             if (newMainImage) {
// //                 updateTask(taskId, { message: 'Optimizing main image...' });
// //                 const compressed = await compressImage(newMainImage);
// //                 imageUrl = await uploadFileWithProgress(taskId, compressed, "products/images", weights.image, (currentFinishedWeight / totalWeight) * 100, totalWeight);
// //                 currentFinishedWeight += weights.image;
// //             }

// //             if (newThumbs?.length) {
// //                 for (let i = 0; i < newThumbs.length; i++) {
// //                     updateTask(taskId, { message: `Uploading thumbnail ${i + 1}/${newThumbs.length}...` });
// //                     const compressed = await compressImage(newThumbs[i]);
// //                     const url = await uploadFileWithProgress(taskId, compressed, "products/thumbnails", weights.thumb, (currentFinishedWeight / totalWeight) * 100, totalWeight);
// //                     thumbnailUrls.push(url);
// //                     currentFinishedWeight += weights.thumb;
// //                 }
// //             }

// //             if (newVideo) {
// //                 updateTask(taskId, { message: 'Uploading video...' });
// //                 videoUrl = await uploadFileWithProgress(taskId, newVideo, "products/videos", weights.video, (currentFinishedWeight / totalWeight) * 100, totalWeight);
// //                 currentFinishedWeight += weights.video;
// //             }

// //             updateTask(taskId, { progress: 95, message: 'Finalizing update...' });

// //             const finalCategories = [];
// //             if (item.newCategory?.trim()) finalCategories.push(item.newCategory.trim());
// //             if (Array.isArray(item.category) && item.category.length) {
// //                 const idSet = new Set(item.category.map(x => Number(x)));
// //                 const names = allCats.filter(c => idSet.has(c.id)).map(c => c.name);
// //                 finalCategories.push(...names);
// //             }

// //             const payload = {
// //                 name: item.name,
// //                 name_ar: item.name_ar,
// //                 description: item.description,
// //                 description_ar: item.description_ar,
// //                 price: item.price,
// //                 price_egp: item.price_egp,
// //                 price_sdg: item.price_sdg,
// //                 color: item.color,
// //                 color_ar: item.color_ar,
// //                 material: item.material,
// //                 material_ar: item.material_ar,
// //                 dimensions: item.dimensions,
// //                 dimensions_ar: item.dimensions_ar,
// //                 categories: JSON.stringify(finalCategories),
// //                 newCategory: item.newCategory,
// //                 newCategoryAr: item.newCategoryAr,
// //                 kept_thumbnails: JSON.stringify(existingThumbs),
// //                 imageUrl: imageUrl || item.imageUrl,
// //                 videoUrl: videoUrl || item.videoUrl,
// //                 thumbnailUrls: thumbnailUrls.length > 0 ? JSON.stringify(thumbnailUrls) : undefined
// //             };

// //             const token = getToken();
// //             const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };

// //             await axios.put(`${CONFIG.API_URL}/api/products/${id}`, payload, { headers });
// //             updateTask(taskId, { status: 'success', progress: 100, message: 'Product updated successfully!' });
// //         } catch (err) {
// //             console.error("Background task failed:", err);
// //             updateTask(taskId, { status: 'error', message: err.message || 'Update failed' });
// //         }
// //     };

// //     const addTask = async (type, data, helpers) => {
// //         const id = generateId();
// //         const newTask = {
// //             id,
// //             task_type: type,
// //             name: data.formData?.name || data.item?.name || 'New Product',
// //             status: 'pending',
// //             progress: 0,
// //             message: 'Queued...',
// //             created_at: new Date()
// //         };

// //         setTasks(prev => [newTask, ...prev]);

// //         // Persist to DB immediately
// //         const token = getToken();
// //         if (token) {
// //             try {
// //                 await axios.post(`${CONFIG.API_URL}/api/admin/tasks`, {
// //                     ...newTask,
// //                     payload: data // Store payload for potential retry
// //                 }, {
// //                     headers: { Authorization: `Bearer ${token}` }
// //                 });
// //             } catch (err) {
// //                 console.error("Failed to persist new task:", err);
// //             }
// //         }

// //         if (type === 'CREATE') {
// //             runCreateTask(id, data, helpers);
// //         } else {
// //             runEditTask(id, data, helpers);
// //         }
// //         return id;
// //     };

// //     return (
// //         <UploadContext.Provider value={{ tasks, addTask, removeTask, clearTasks, fetchHistory }}>
// //             {children}
// //         </UploadContext.Provider>
// //     );
// // };








// import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
// import axios from 'axios';
// import CONFIG from '../config';
// import { getToken } from '../utils/auth';

// const UploadContext = createContext();

// export const useUpload = () => {
//     const context = useContext(UploadContext);
//     if (!context) throw new Error('useUpload must be used within an UploadProvider');
//     return context;
// };

// // Simple debounce for state updates to avoid React stutter
// const debounceStates = new Map();
// const debouncedUpdate = (id, updates, setter) => {
//     if (debounceStates.has(id)) clearTimeout(debounceStates.get(id));
//     debounceStates.set(id, setTimeout(() => {
//         setter(prev => prev.map(t => t.id === id ? { ...t, ...updates } : t));
//     }, 50));
// };

// // Helper to unique ID
// const generateId = () => Math.random().toString(36).substr(2, 9);

// export const UploadProvider = ({ children }) => {
//     const [tasks, setTasks] = useState([]);

//     useEffect(() => {
//         const loadPersistedTasks = async () => {
//             const token = getToken();
//             if (!token) return;
//             try {
//                 const res = await axios.get(`${CONFIG.API_URL}/api/admin/tasks`, {
//                     headers: { Authorization: `Bearer ${token}` }
//                 });
//                 const persisted = res.data.map(t => ({
//                     ...t,
//                     // If a task was 'running' but we just reloaded, it means it was interrupted
//                     status: t.status === 'running' || t.status === 'pending' ? 'interrupted' : t.status,
//                     message: (t.status === 'running' || t.status === 'pending') ? 'Task interrupted' : t.message
//                 }));
//                 setTasks(persisted);
//             } catch (err) {
//                 console.error("Failed to load tasks:", err);
//             }
//         };
//         loadPersistedTasks();
//     }, []);

//     const updateTask = useCallback(async (id, updates) => {
//         setTasks(prev => prev.map(t => t.id === id ? { ...t, ...updates } : t));

//         // Persist to DB
//         const token = getToken();
//         if (token) {
//             try {
//                 await axios.patch(`${CONFIG.API_URL}/api/admin/tasks/${id}`, updates, {
//                     headers: { Authorization: `Bearer ${token}` }
//                 });
//             } catch (err) {
//                 console.error("Failed to sync task update:", err);
//             }
//         }
//     }, []);

//     const updateTaskProgress = useCallback((id, updates) => {
//         debouncedUpdate(id, updates, setTasks);

//         // Persist progress to DB at milestones to avoid 5% stuck on reload
//         if (updates.progress) {
//             const p = Math.round(updates.progress);
//             if ([25, 50, 75, 95].includes(p)) {
//                 updateTask(id, { progress: p });
//             }
//         }
//     }, [updateTask]);

//     const removeTask = useCallback(async (id) => {
//         setTasks(prev => prev.filter(t => t.id !== id));
//         const token = getToken();
//         if (token) {
//             try {
//                 await axios.delete(`${CONFIG.API_URL}/api/admin/tasks/${id}`, {
//                     headers: { Authorization: `Bearer ${token}` }
//                 });
//             } catch (err) {
//                 console.error("Failed to delete task from DB:", err);
//             }
//         }
//     }, []);

//     const clearTasks = useCallback(async () => {
//         setTasks([]);
//         const token = getToken();
//         if (token) {
//             try {
//                 await axios.delete(`${CONFIG.API_URL}/api/admin/tasks`, {
//                     headers: { Authorization: `Bearer ${token}` }
//                 });
//             } catch (err) {
//                 console.error("Failed to clear tasks from DB:", err);
//             }
//         }
//     }, []);

//     const fetchHistory = useCallback(async () => {
//         const token = getToken();
//         if (!token) return;
//         try {
//             const res = await axios.get(`${CONFIG.API_URL}/api/admin/tasks`, {
//                 headers: { Authorization: `Bearer ${token}` }
//             });
//             const persisted = res.data.map(t => ({
//                 ...t,
//                 status: t.status === 'running' || t.status === 'pending' ? 'interrupted' : t.status,
//                 message: (t.status === 'running' || t.status === 'pending') ? 'Task interrupted' : t.message
//             }));

//             // Merge: keep tasks that are currently running in memory
//             setTasks(prev => {
//                 const runningIds = new Set(prev.filter(pt => pt.status === 'running').map(pt => pt.id));
//                 const updatedPersisted = persisted.filter(pt => !runningIds.has(pt.id));
//                 const currentRunning = prev.filter(pt => pt.status === 'running');
//                 return [...currentRunning, ...updatedPersisted];
//             });
//         } catch (err) {
//             console.error("Failed to fetch task history:", err);
//         }
//     }, []);

//     const uploadFileWithProgress = async (taskId, file, folder, weight, startProgress, totalWeight) => {
//         try {
//             const token = getToken();
//             const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

//             const presignRes = await axios.post(
//                 `${CONFIG.API_URL}/api/admin/storage/presign`,
//                 { filename: file.name, mimetype: file.type, folder },
//                 { headers: authHeaders }
//             );

//             const { uploadUrl, fileUrl, method, headers, formData } = presignRes.data;

//             const onUploadProgress = (progressEvent) => {
//                 const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
//                 const taskProgress = startProgress + (percentCompleted * weight / totalWeight);
//                 updateTaskProgress(taskId, { progress: Math.min(taskProgress, 95) });
//             };

//             const sizeMB = file.size / (1024 * 1024);
//             // Dynamic timeout: 30 seconds per MB, minimum 2 minutes, maximum 24 hours
//             const uploadTimeout = Math.max(120000, Math.min(Math.floor(sizeMB * 30000), 86400000));

//             if (formData) {
//                 const fd = new FormData();
//                 Object.entries(formData).forEach(([k, v]) => fd.append(k, v));
//                 fd.append("file", file);
//                 await axios.post(uploadUrl, fd, {
//                     headers: { "Content-Type": "multipart/form-data" },
//                     onUploadProgress,
//                     timeout: uploadTimeout
//                 });
//             } else {
//                 await axios({
//                     method: method || "PUT",
//                     url: uploadUrl,
//                     data: file,
//                     headers: { "Content-Type": file.type, ...(headers || {}) },
//                     onUploadProgress,
//                     timeout: uploadTimeout
//                 });
//             }

//             return fileUrl;
//         } catch (err) {
//             console.error("Upload error detail:", err.response?.data || err.message);
//             const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
//             let errorMessage = `Failed to upload ${file.name} (${sizeMB}MB). `;

//             if (err.code === 'ECONNABORTED') {
//                 errorMessage += "Connection timed out. Your internet may be too slow for this large file.";
//             } else if (err.response?.status === 413) {
//                 errorMessage += "File too large for the storage provider.";
//             } else if (err.response?.data?.error?.message) {
//                 errorMessage += err.response.data.error.message;
//             } else {
//                 errorMessage += "Connection interrupted or storage limit reached.";
//             }

//             throw new Error(errorMessage);
//         }
//     };

//     const runCreateTask = async (taskId, taskData, componentHelpers) => {
//         const { compressImage } = componentHelpers;
//         const { formData } = taskData;

//         try {
//             updateTask(taskId, { status: 'running', message: 'Preparing media...', progress: 5 });

//             // Calculate weights
//             const weights = { image: 10, thumb: 10, video: 50, api: 5 };
//             let totalWeight = weights.api;
//             if (formData.image) totalWeight += weights.image;
//             if (formData.thumbnails?.length) totalWeight += weights.thumb * formData.thumbnails.length;
//             if (formData.video) totalWeight += weights.video;

//             let currentFinishedWeight = 0;
//             let imageUrl = null;
//             let videoUrl = null;
//             const thumbnailUrls = [];

//             // 1. Image
//             if (formData.image) {
//                 updateTask(taskId, { message: 'Optimizing main image...' });
//                 const compressed = await compressImage(formData.image);
//                 imageUrl = await uploadFileWithProgress(taskId, compressed, "products/images", weights.image, (currentFinishedWeight / totalWeight) * 100, totalWeight);
//                 currentFinishedWeight += weights.image;
//             }

//             // 2. Thumbnails
//             if (formData.thumbnails?.length) {
//                 for (let i = 0; i < formData.thumbnails.length; i++) {
//                     updateTask(taskId, { message: `Uploading thumbnail ${i + 1}/${formData.thumbnails.length}...` });
//                     const compressed = await compressImage(formData.thumbnails[i]);
//                     const url = await uploadFileWithProgress(taskId, compressed, "products/thumbnails", weights.thumb, (currentFinishedWeight / totalWeight) * 100, totalWeight);
//                     thumbnailUrls.push(url);
//                     currentFinishedWeight += weights.thumb;
//                 }
//             }

//             // 3. Video
//             if (formData.video) {
//                 updateTask(taskId, { message: 'Uploading video...' });
//                 videoUrl = await uploadFileWithProgress(taskId, formData.video, "products/videos", weights.video, (currentFinishedWeight / totalWeight) * 100, totalWeight);
//                 currentFinishedWeight += weights.video;
//             }

//             updateTask(taskId, { progress: 95, message: 'Finalizing product...' });

//             const payload = {
//                 name: formData.name,
//                 name_ar: formData.name_ar,
//                 description: formData.description,
//                 description_ar: formData.description_ar,
//                 price: formData.price,
//                 price_egp: formData.price_egp,
//                 price_sdg: formData.price_sdg,
//                 color: formData.color,
//                 color_ar: formData.color_ar,
//                 material: formData.material,
//                 material_ar: formData.material_ar,
//                 dimensions: formData.dimensions,
//                 dimensions_ar: formData.dimensions_ar,
//                 imageUrl,
//                 videoUrl,
//                 thumbnailUrls: thumbnailUrls.length > 0 ? JSON.stringify(thumbnailUrls) : undefined
//             };

//             let finalCategories = [];
//             if (formData.newCategory?.trim()) finalCategories.push(formData.newCategory.trim());
//             if (Array.isArray(formData.category) && formData.category.length > 0) {
//                 const idSet = new Set(formData.category.map(x => String(x)));
//                 finalCategories.push(...Array.from(idSet));
//             }
//             payload.categories = JSON.stringify(Array.from(new Set(finalCategories.map(s => s.trim()).filter(Boolean))));

//             const headers = {};
//             const token = getToken();
//             if (token) headers.Authorization = `Bearer ${token}`;

//             await axios.post(`${CONFIG.API_URL}/api/products`, payload, { headers });
//             updateTask(taskId, { status: 'success', progress: 100, message: 'Product created successfully!' });
//         } catch (err) {
//             console.error("Background task failed:", err);
//             updateTask(taskId, { status: 'error', message: err.message || 'Upload failed' });
//         }
//     };

//     const runEditTask = async (taskId, taskData, componentHelpers) => {
//         const { compressImage } = componentHelpers;
//         const { id, item, initialData } = taskData;
//         const { newMainImage, newVideo, newThumbs, existingThumbs } = initialData;

//         try {
//             updateTask(taskId, { status: 'running', message: 'Processing changes...', progress: 5 });

//             const weights = { image: 15, thumb: 15, video: 50, api: 5 };
//             let totalWeight = weights.api;
//             if (newMainImage) totalWeight += weights.image;
//             if (newThumbs?.length) totalWeight += weights.thumb * newThumbs.length;
//             if (newVideo) totalWeight += weights.video;

//             let currentFinishedWeight = 0;
//             let imageUrl = undefined;
//             let videoUrl = undefined;
//             const thumbnailUrls = [];

//             if (newMainImage) {
//                 updateTask(taskId, { message: 'Optimizing main image...' });
//                 const compressed = await compressImage(newMainImage);
//                 imageUrl = await uploadFileWithProgress(taskId, compressed, "products/images", weights.image, (currentFinishedWeight / totalWeight) * 100, totalWeight);
//                 currentFinishedWeight += weights.image;
//             }

//             if (newThumbs?.length) {
//                 for (let i = 0; i < newThumbs.length; i++) {
//                     updateTask(taskId, { message: `Uploading thumbnail ${i + 1}/${newThumbs.length}...` });
//                     const compressed = await compressImage(newThumbs[i]);
//                     const url = await uploadFileWithProgress(taskId, compressed, "products/thumbnails", weights.thumb, (currentFinishedWeight / totalWeight) * 100, totalWeight);
//                     thumbnailUrls.push(url);
//                     currentFinishedWeight += weights.thumb;
//                 }
//             }

//             if (newVideo) {
//                 updateTask(taskId, { message: 'Uploading video...' });
//                 videoUrl = await uploadFileWithProgress(taskId, newVideo, "products/videos", weights.video, (currentFinishedWeight / totalWeight) * 100, totalWeight);
//                 currentFinishedWeight += weights.video;
//             }

//             updateTask(taskId, { progress: 95, message: 'Finalizing update...' });

//             const finalCategories = [];
//             if (item.newCategory?.trim()) finalCategories.push(item.newCategory.trim());
//             if (Array.isArray(item.category) && item.category.length) {
//                 const idSet = new Set(item.category.map(x => String(x)));
//                 finalCategories.push(...Array.from(idSet));
//             }

//             const payload = {
//                 name: item.name,
//                 name_ar: item.name_ar,
//                 description: item.description,
//                 description_ar: item.description_ar,
//                 price: item.price,
//                 price_egp: item.price_egp,
//                 price_sdg: item.price_sdg,
//                 color: item.color,
//                 color_ar: item.color_ar,
//                 material: item.material,
//                 material_ar: item.material_ar,
//                 dimensions: item.dimensions,
//                 dimensions_ar: item.dimensions_ar,
//                 categories: JSON.stringify(finalCategories),
//                 newCategory: item.newCategory,
//                 newCategoryAr: item.newCategoryAr,
//                 kept_thumbnails: JSON.stringify(existingThumbs),
//                 imageUrl: imageUrl || item.imageUrl,
//                 videoUrl: videoUrl || item.videoUrl,
//                 thumbnailUrls: thumbnailUrls.length > 0 ? JSON.stringify(thumbnailUrls) : undefined
//             };

//             const token = getToken();
//             const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };

//             await axios.put(`${CONFIG.API_URL}/api/products/${id}`, payload, { headers });
//             updateTask(taskId, { status: 'success', progress: 100, message: 'Product updated successfully!' });
//         } catch (err) {
//             console.error("Background task failed:", err);
//             updateTask(taskId, { status: 'error', message: err.message || 'Update failed' });
//         }
//     };

//     const addTask = async (type, data, helpers) => {
//         const id = generateId();
//         const newTask = {
//             id,
//             task_type: type,
//             name: data.formData?.name || data.item?.name || 'New Product',
//             status: 'pending',
//             progress: 0,
//             message: 'Queued...',
//             created_at: new Date()
//         };

//         setTasks(prev => [newTask, ...prev]);

//         // Persist to DB immediately
//         const token = getToken();
//         if (token) {
//             try {
//                 await axios.post(`${CONFIG.API_URL}/api/admin/tasks`, {
//                     ...newTask,
//                     payload: data // Store payload for potential retry
//                 }, {
//                     headers: { Authorization: `Bearer ${token}` }
//                 });
//             } catch (err) {
//                 console.error("Failed to persist new task:", err);
//             }
//         }

//         if (type === 'CREATE') {
//             runCreateTask(id, data, helpers);
//         } else {
//             runEditTask(id, data, helpers);
//         }
//         return id;
//     };

//     return (
//         <UploadContext.Provider value={{ tasks, addTask, removeTask, clearTasks, fetchHistory }}>
//             {children}
//         </UploadContext.Provider>
//     );
// };











// import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
// import axios from 'axios';
// import CONFIG from '../config';
// import { getToken } from '../utils/auth';

// const UploadContext = createContext();

// export const useUpload = () => {
//     const context = useContext(UploadContext);
//     if (!context) throw new Error('useUpload must be used within an UploadProvider');
//     return context;
// };

// // Simple debounce for state updates to avoid React stutter
// const debounceStates = new Map();
// const debouncedUpdate = (id, updates, setter) => {
//     if (debounceStates.has(id)) clearTimeout(debounceStates.get(id));
//     debounceStates.set(id, setTimeout(() => {
//         setter(prev => prev.map(t => t.id === id ? { ...t, ...updates } : t));
//     }, 50));
// };

// // Helper to unique ID
// const generateId = () => Math.random().toString(36).substr(2, 9);

// export const UploadProvider = ({ children }) => {
//     const [tasks, setTasks] = useState([]);

//     useEffect(() => {
//         const loadPersistedTasks = async () => {
//             const token = getToken();
//             if (!token) return;
//             try {
//                 const res = await axios.get(`${CONFIG.API_URL}/api/admin/tasks`, {
//                     headers: { Authorization: `Bearer ${token}` }
//                 });
//                 const persisted = res.data.map(t => ({
//                     ...t,
//                     // If a task was 'running' but we just reloaded, it means it was interrupted
//                     status: t.status === 'running' || t.status === 'pending' ? 'interrupted' : t.status,
//                     message: (t.status === 'running' || t.status === 'pending') ? 'Task interrupted' : t.message
//                 }));
//                 setTasks(persisted);
//             } catch (err) {
//                 console.error("Failed to load tasks:", err);
//             }
//         };
//         loadPersistedTasks();
//     }, []);

//     const updateTask = useCallback(async (id, updates) => {
//         setTasks(prev => prev.map(t => t.id === id ? { ...t, ...updates } : t));

//         // Persist to DB
//         const token = getToken();
//         if (token) {
//             try {
//                 await axios.patch(`${CONFIG.API_URL}/api/admin/tasks/${id}`, updates, {
//                     headers: { Authorization: `Bearer ${token}` }
//                 });
//             } catch (err) {
//                 console.error("Failed to sync task update:", err);
//             }
//         }
//     }, []);

//     const updateTaskProgress = useCallback((id, updates) => {
//         debouncedUpdate(id, updates, setTasks);

//         // Persist progress to DB at milestones to avoid 5% stuck on reload
//         if (updates.progress) {
//             const p = Math.round(updates.progress);
//             if ([25, 50, 75, 95].includes(p)) {
//                 updateTask(id, { progress: p });
//             }
//         }
//     }, [updateTask]);

//     const removeTask = useCallback(async (id) => {
//         setTasks(prev => prev.filter(t => t.id !== id));
//         const token = getToken();
//         if (token) {
//             try {
//                 await axios.delete(`${CONFIG.API_URL}/api/admin/tasks/${id}`, {
//                     headers: { Authorization: `Bearer ${token}` }
//                 });
//             } catch (err) {
//                 console.error("Failed to delete task from DB:", err);
//             }
//         }
//     }, []);

//     const clearTasks = useCallback(async () => {
//         setTasks([]);
//         const token = getToken();
//         if (token) {
//             try {
//                 await axios.delete(`${CONFIG.API_URL}/api/admin/tasks`, {
//                     headers: { Authorization: `Bearer ${token}` }
//                 });
//             } catch (err) {
//                 console.error("Failed to clear tasks from DB:", err);
//             }
//         }
//     }, []);

//     const fetchHistory = useCallback(async () => {
//         const token = getToken();
//         if (!token) return;
//         try {
//             const res = await axios.get(`${CONFIG.API_URL}/api/admin/tasks`, {
//                 headers: { Authorization: `Bearer ${token}` }
//             });
//             const persisted = res.data.map(t => ({
//                 ...t,
//                 status: t.status === 'running' || t.status === 'pending' ? 'interrupted' : t.status,
//                 message: (t.status === 'running' || t.status === 'pending') ? 'Task interrupted' : t.message
//             }));

//             // Merge: keep tasks that are currently running in memory
//             setTasks(prev => {
//                 const runningIds = new Set(prev.filter(pt => pt.status === 'running').map(pt => pt.id));
//                 const updatedPersisted = persisted.filter(pt => !runningIds.has(pt.id));
//                 const currentRunning = prev.filter(pt => pt.status === 'running');
//                 return [...currentRunning, ...updatedPersisted];
//             });
//         } catch (err) {
//             console.error("Failed to fetch task history:", err);
//         }
//     }, []);

//     const uploadFileWithProgress = async (taskId, file, folder, weight, startProgress, totalWeight) => {
//         try {
//             const token = getToken();
//             const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

//             const presignRes = await axios.post(
//                 `${CONFIG.API_URL}/api/storage/presign`,
//                 { filename: file.name, mimetype: file.type, folder },
//                 { headers: authHeaders }
//             );

//             const { uploadUrl, fileUrl, method, headers, formData } = presignRes.data;

//             const onUploadProgress = (progressEvent) => {
//                 const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
//                 const taskProgress = startProgress + (percentCompleted * weight / totalWeight);
//                 updateTaskProgress(taskId, { progress: Math.min(taskProgress, 95) });
//             };

//             const sizeMB = file.size / (1024 * 1024);
//             // Dynamic timeout: 30 seconds per MB, minimum 2 minutes, maximum 24 hours
//             const uploadTimeout = Math.max(120000, Math.min(Math.floor(sizeMB * 30000), 86400000));

//             if (formData) {
//                 const fd = new FormData();
//                 Object.entries(formData).forEach(([k, v]) => fd.append(k, v));
//                 fd.append("file", file);
//                 await axios.post(uploadUrl, fd, {
//                     headers: { "Content-Type": "multipart/form-data" },
//                     onUploadProgress,
//                     timeout: uploadTimeout
//                 });
//             } else {
//                 await axios({
//                     method: method || "PUT",
//                     url: uploadUrl,
//                     data: file,
//                     headers: { "Content-Type": file.type, ...(headers || {}) },
//                     onUploadProgress,
//                     timeout: uploadTimeout
//                 });
//             }

//             return fileUrl;
//         } catch (err) {
//             console.error("Upload error detail:", err.response?.data || err.message);
//             const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
//             let errorMessage = `Failed to upload ${file.name} (${sizeMB}MB). `;

//             if (err.code === 'ECONNABORTED') {
//                 errorMessage += "Connection timed out. Your internet may be too slow for this large file.";
//             } else if (err.response?.status === 413) {
//                 errorMessage += "File too large for the storage provider.";
//             } else if (err.response?.data?.error?.message) {
//                 errorMessage += err.response.data.error.message;
//             } else {
//                 errorMessage += "Connection interrupted or storage limit reached.";
//             }

//             throw new Error(errorMessage);
//         }
//     };

//     const runCreateTask = async (taskId, taskData, componentHelpers) => {
//         const { compressImage } = componentHelpers;
//         const { formData, categories } = taskData;

//         try {
//             updateTask(taskId, { status: 'running', message: 'Preparing media...', progress: 5 });

//             // Calculate weights
//             const weights = { image: 10, thumb: 10, video: 50, api: 5 };
//             let totalWeight = weights.api;
//             if (formData.image) totalWeight += weights.image;
//             if (formData.thumbnails?.length) totalWeight += weights.thumb * formData.thumbnails.length;
//             if (formData.video) totalWeight += weights.video;

//             let currentFinishedWeight = 0;
//             let imageUrl = null;
//             let videoUrl = null;
//             const thumbnailUrls = [];

//             // 1. Image
//             if (formData.image) {
//                 updateTask(taskId, { message: 'Optimizing main image...' });
//                 const compressed = await compressImage(formData.image);
//                 imageUrl = await uploadFileWithProgress(taskId, compressed, "products/images", weights.image, (currentFinishedWeight / totalWeight) * 100, totalWeight);
//                 currentFinishedWeight += weights.image;
//             }

//             // 2. Thumbnails
//             if (formData.thumbnails?.length) {
//                 for (let i = 0; i < formData.thumbnails.length; i++) {
//                     updateTask(taskId, { message: `Uploading thumbnail ${i + 1}/${formData.thumbnails.length}...` });
//                     const compressed = await compressImage(formData.thumbnails[i]);
//                     const url = await uploadFileWithProgress(taskId, compressed, "products/thumbnails", weights.thumb, (currentFinishedWeight / totalWeight) * 100, totalWeight);
//                     thumbnailUrls.push(url);
//                     currentFinishedWeight += weights.thumb;
//                 }
//             }

//             // 3. Video
//             if (formData.video) {
//                 updateTask(taskId, { message: 'Uploading video...' });
//                 videoUrl = await uploadFileWithProgress(taskId, formData.video, "products/videos", weights.video, (currentFinishedWeight / totalWeight) * 100, totalWeight);
//                 currentFinishedWeight += weights.video;
//             }

//             updateTask(taskId, { progress: 95, message: 'Finalizing product...' });

//             const payload = {
//                 name: formData.name,
//                 name_ar: formData.name_ar,
//                 description: formData.description,
//                 description_ar: formData.description_ar,
//                 price: formData.price,
//                 price_egp: formData.price_egp,
//                 price_sdg: formData.price_sdg,
//                 color: formData.color,
//                 color_ar: formData.color_ar,
//                 material: formData.material,
//                 material_ar: formData.material_ar,
//                 dimensions: formData.dimensions,
//                 dimensions_ar: formData.dimensions_ar,
//                 imageUrl,
//                 videoUrl,
//                 thumbnailUrls: thumbnailUrls.length > 0 ? JSON.stringify(thumbnailUrls) : undefined
//             };

//             let finalCategories = [];
//             if (formData.newCategory?.trim()) finalCategories.push(formData.newCategory.trim());
//             if (Array.isArray(formData.category) && formData.category.length > 0) {
//                 const idSet = new Set(formData.category.map(x => Number(x)));
//                 const names = categories.filter(c => idSet.has(c.id)).map(c => c.name);
//                 finalCategories.push(...names);
//             }
//             payload.categories = JSON.stringify(Array.from(new Set(finalCategories.map(s => s.trim()).filter(Boolean))));

//             const headers = {};
//             const token = getToken();
//             if (token) headers.Authorization = `Bearer ${token}`;

//             await axios.post(`${CONFIG.API_URL}/api/products`, payload, { headers });
//             updateTask(taskId, { status: 'success', progress: 100, message: 'Product created successfully!' });
//         } catch (err) {
//             console.error("Background task failed:", err);
//             updateTask(taskId, { status: 'error', message: err.message || 'Upload failed' });
//         }
//     };

//     const runEditTask = async (taskId, taskData, componentHelpers) => {
//         const { compressImage } = componentHelpers;
//         const { id, item, initialData, categories: allCats } = taskData;
//         const { newMainImage, newVideo, newThumbs, existingThumbs } = initialData;

//         try {
//             updateTask(taskId, { status: 'running', message: 'Processing changes...', progress: 5 });

//             const weights = { image: 15, thumb: 15, video: 50, api: 5 };
//             let totalWeight = weights.api;
//             if (newMainImage) totalWeight += weights.image;
//             if (newThumbs?.length) totalWeight += weights.thumb * newThumbs.length;
//             if (newVideo) totalWeight += weights.video;

//             let currentFinishedWeight = 0;
//             let imageUrl = undefined;
//             let videoUrl = undefined;
//             const thumbnailUrls = [];

//             if (newMainImage) {
//                 updateTask(taskId, { message: 'Optimizing main image...' });
//                 const compressed = await compressImage(newMainImage);
//                 imageUrl = await uploadFileWithProgress(taskId, compressed, "products/images", weights.image, (currentFinishedWeight / totalWeight) * 100, totalWeight);
//                 currentFinishedWeight += weights.image;
//             }

//             if (newThumbs?.length) {
//                 for (let i = 0; i < newThumbs.length; i++) {
//                     updateTask(taskId, { message: `Uploading thumbnail ${i + 1}/${newThumbs.length}...` });
//                     const compressed = await compressImage(newThumbs[i]);
//                     const url = await uploadFileWithProgress(taskId, compressed, "products/thumbnails", weights.thumb, (currentFinishedWeight / totalWeight) * 100, totalWeight);
//                     thumbnailUrls.push(url);
//                     currentFinishedWeight += weights.thumb;
//                 }
//             }

//             if (newVideo) {
//                 updateTask(taskId, { message: 'Uploading video...' });
//                 videoUrl = await uploadFileWithProgress(taskId, newVideo, "products/videos", weights.video, (currentFinishedWeight / totalWeight) * 100, totalWeight);
//                 currentFinishedWeight += weights.video;
//             }

//             updateTask(taskId, { progress: 95, message: 'Finalizing update...' });

//             const finalCategories = [];
//             if (item.newCategory?.trim()) finalCategories.push(item.newCategory.trim());
//             if (Array.isArray(item.category) && item.category.length) {
//                 const idSet = new Set(item.category.map(x => Number(x)));
//                 const names = allCats.filter(c => idSet.has(c.id)).map(c => c.name);
//                 finalCategories.push(...names);
//             }

//             const payload = {
//                 name: item.name,
//                 name_ar: item.name_ar,
//                 description: item.description,
//                 description_ar: item.description_ar,
//                 price: item.price,
//                 price_egp: item.price_egp,
//                 price_sdg: item.price_sdg,
//                 color: item.color,
//                 color_ar: item.color_ar,
//                 material: item.material,
//                 material_ar: item.material_ar,
//                 dimensions: item.dimensions,
//                 dimensions_ar: item.dimensions_ar,
//                 categories: JSON.stringify(finalCategories),
//                 newCategory: item.newCategory,
//                 newCategoryAr: item.newCategoryAr,
//                 kept_thumbnails: JSON.stringify(existingThumbs),
//                 imageUrl: imageUrl || item.imageUrl,
//                 videoUrl: videoUrl || item.videoUrl,
//                 thumbnailUrls: thumbnailUrls.length > 0 ? JSON.stringify(thumbnailUrls) : undefined
//             };

//             const token = getToken();
//             const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };

//             await axios.put(`${CONFIG.API_URL}/api/products/${id}`, payload, { headers });
//             updateTask(taskId, { status: 'success', progress: 100, message: 'Product updated successfully!' });
//         } catch (err) {
//             console.error("Background task failed:", err);
//             updateTask(taskId, { status: 'error', message: err.message || 'Update failed' });
//         }
//     };

//     const addTask = async (type, data, helpers) => {
//         const id = generateId();
//         const newTask = {
//             id,
//             task_type: type,
//             name: data.formData?.name || data.item?.name || 'New Product',
//             status: 'pending',
//             progress: 0,
//             message: 'Queued...',
//             created_at: new Date()
//         };

//         setTasks(prev => [newTask, ...prev]);

//         // Persist to DB immediately
//         const token = getToken();
//         if (token) {
//             try {
//                 await axios.post(`${CONFIG.API_URL}/api/admin/tasks`, {
//                     ...newTask,
//                     payload: data // Store payload for potential retry
//                 }, {
//                     headers: { Authorization: `Bearer ${token}` }
//                 });
//             } catch (err) {
//                 console.error("Failed to persist new task:", err);
//             }
//         }

//         if (type === 'CREATE') {
//             runCreateTask(id, data, helpers);
//         } else {
//             runEditTask(id, data, helpers);
//         }
//         return id;
//     };

//     return (
//         <UploadContext.Provider value={{ tasks, addTask, removeTask, clearTasks, fetchHistory }}>
//             {children}
//         </UploadContext.Provider>
//     );
// };







